import { forwardRef, useImperativeHandle } from 'react';
import { Controller } from 'react-hook-form';

import { Button, Grid, TextField } from '@mui/material';
import dayjs from 'dayjs';

import { makeAuthenticatedApiCall } from '../../apiHelper';
import { apiEnergyMapping, apiTransmissionMapping } from '../../constants/cotation';
import { formatRegPlate } from '../../utils/formatRegPlate';

// Mapping des couleurs de l'API vers nos valeurs
const apiColourMapping: { [key: string]: string } = {
  'GRIS CLAIR': 'light_grey',
  'GRIS FONCE': 'dark_grey',
  'BLEU CLAIR': 'light_blue',
  'BLEU FONCE': 'dark_blue',
  'ROUGE CLAIR': 'light_red',
  'ROUGE FONCE': 'dark_red',
  'VERT CLAIR': 'light_green',
  'VERT FONCE': 'dark_green',
  'BEIGE CLAIR': 'light_beige',
  'JAUNE CLAIR': 'light_yellow',
  'MARRON CLAIR': 'light_brown',
  'MARRON FONCE': 'dark_brown',
  JAUN: 'yellow',
  BLAN: 'white',
  ORAN: 'orange',
  GRIS: 'grey',
  BLEU: 'blue',
  ROUGE: 'red',
  VERT: 'green',
  BEIGE: 'beige',
  MARRON: 'brown',
  NOIR: 'black',
  VIOLET: 'purple',
};

interface VehicleInfoResponse {
  VIN: string;
  Modele: string;
  Version: string;
  NbPlacesAss: number;
  Genre: string;
  NbPortes: number;
  DateMec: string;
  Marque: string;
  Couleur: string;
  Energie: string;
  TPBoiteVit: string;
  Carrosserie?: string;
  CarrosserieCG?: string;
  CO2?: string;
  Cylindree?: string;
  NbCylind?: string;
  PoidsVide?: string;
  PuissFisc?: string;
  Type?: string;
  NSerie?: string;
  CodeMoteur?: string;
  PuissanceKW?: string;
  PuissCh?: string;
  PTR?: string;
  ModeInjection?: string;
  NbSoupapes?: string;
  TurboCompr?: string;
}

interface RegPlateProps {
  control: any;
  errors: any;
  trigger: any;
  getValues: any;
  setValue: any;
  isApiPlaqueLoading: boolean;
  setIsApiPlaqueLoading: (loading: boolean) => void;
  setApiPlaqueError: (error: string) => void;
  genreOptions: Array<{ label: string; value: string }>;
  options: Array<{ label: string; value: string }>;
  coloursOptions: Array<{ label: string; value: string }>;
  fuelOptions: Array<{ label: string; value: string }>;
  transmissionOptions: Array<{ label: string; value: string }>;
}

interface RegPlateRef {
  handleGetRegPlateInformations: () => Promise<any>;
}

const RegPlate = forwardRef<RegPlateRef, RegPlateProps>(
  (
    {
      control,
      errors,
      trigger,
      getValues,
      setValue,
      isApiPlaqueLoading,
      setIsApiPlaqueLoading,
      setApiPlaqueError,
      genreOptions,
      options,
      coloursOptions,
      fuelOptions,
      transmissionOptions,
    },
    ref,
  ) => {
    const canBeFormatted = (regPlate: string) => {
      return regPlate && regPlate.length >= 7 && regPlate.length <= 9;
    };

    const resetApiFields = () => {
      // Réinitialisation de tous les champs qui sont remplis par l'API
      setValue('vin', '');
      setValue('model', '');
      setValue('derivative', '');
      setValue('registration_plate', '');
      setValue('seats', '');
      setValue('doors', '');
      setValue('reg_date', null);
      setValue('genre', { value: '', label: '' });
      setValue('make', { value: '', label: '' });
      setValue('fuel', { value: '', label: '' });
      setValue('transmission', { value: '', label: '' });
      setValue('colour', { value: '', label: '' });
    };

    const handleGetRegPlateInformations = async () => {
      try {
        const regPlateValue = getValues('reg_plate');
        if (!regPlateValue) {
          setApiPlaqueError("Veuillez entrer une plaque d'immatriculation");
          return null;
        }

        const formattedRegPlate = formatRegPlate(regPlateValue);

        try {
          // Tentative avec l'API principale
          const infoImmatResponse = await makeAuthenticatedApiCall<VehicleInfoResponse>(
            'get',
            `/api/plaque/regplate?regPlate=${encodeURIComponent(formattedRegPlate?.toString() ?? regPlateValue)}`,
          );
          const vehicleInfoPositiveResponse = infoImmatResponse.data;
          return await processVehicleInfo(vehicleInfoPositiveResponse, formattedRegPlate ?? regPlateValue);
        } catch (error: any) {
          const status = error.response?.status;
          const apiMessage = error.response?.data?.message;
          // 404 = plaque inconnue
          if (status === 404) {
            setApiPlaqueError(apiMessage || "Plaque immatriculation inconnue.");
            return null;
          }
          // 504 = timeout, 503 = service indisponible
          if (status === 504 || status === 503) {
            setApiPlaqueError(apiMessage || (status === 504 ? "Le service ne répond pas (délai dépassé)." : "Le service est temporairement indisponible."));
            return null;
          }
          // Si l'erreur est 500 ou contient le message spécifique, on tente l'API MISIV
          if (
            error.response?.status === 500 ||
            error.message?.includes('Not enough valid data for insertion') ||
            (error.response?.data?.message === 'getRegPlateController : Erreur interne du serveur' &&
              error.response?.data?.error === 'Not enough valid data for insertion')
          ) {
            console.log("Tentative avec l'API MISIV après échec de l'API principale");
            try {
              const misivResponse = await makeAuthenticatedApiCall<VehicleInfoResponse>(
                'get',
                `/api/misiv/regplate?regPlate=${encodeURIComponent(formattedRegPlate?.toString() ?? regPlateValue)}`,
              );
              const vehicleInfoFromMisiv = misivResponse.data;
              return await processVehicleInfo(vehicleInfoFromMisiv, formattedRegPlate ?? regPlateValue);
            } catch (misivError: any) {
              const misivStatus = misivError.response?.status;
              const misivMsg = misivError.response?.data?.message ?? misivError.message;
              console.error('RegPlate MISIV:', misivStatus ?? 'network', misivMsg);
              throw error; // On propage l'erreur originale si MISIV échoue aussi
            }
          }
          // Autre erreur : message backend si dispo, sinon générique
          setApiPlaqueError(error.response?.data?.message || "Une erreur s'est produite lors de la récupération des informations.");
          return null;
        }
      } catch (error: any) {
        const status = error.response?.status;
        const msg = error.response?.data?.message ?? error.message;
        if (status != null) {
          console.error('RegPlate API:', status, msg);
        } else {
          console.error('RegPlate API:', msg);
        }
        setApiPlaqueError(error.response?.data?.message || "Une erreur s'est produite lors de la récupération des informations.");
        return null;
      }
    };

    // Fonction pour traiter les informations du véhicule (pour éviter la duplication de code)
    const processVehicleInfo = async (vehicleInfoResponse: any, regPlateValue: string) => {
      if (vehicleInfoResponse) {
        const hasBasicInfo = vehicleInfoResponse.VIN || vehicleInfoResponse.Marque || vehicleInfoResponse.Modele;

        if (!hasBasicInfo) {
          setApiPlaqueError("Aucune donnée trouvée pour cette plaque d'immatriculation.");
          return null;
        }

        // Reset de l'erreur si on a des données
        setApiPlaqueError('');

        // Créer le texte pour more_api_infos avec toutes les informations disponibles
        const moreApiInfos = [
          `Énergie: ${vehicleInfoResponse.Energie || 'N/A'}`,
          `CO2: ${vehicleInfoResponse.CO2 || 'N/A'}`,
          `GenreVCG: ${vehicleInfoResponse.Genre || 'N/A'}`,
          `Puissance Fiscale: ${vehicleInfoResponse.PuissFisc || 'N/A'}`,
          `CarrosserieCG: ${vehicleInfoResponse.CarrosserieCG || 'N/A'}`,
          `PuisFiscReel: ${vehicleInfoResponse.PuissanceKW || 'N/A'} KW / ${vehicleInfoResponse.PuissCh || 'N/A'} CH`,
          `Type Mine: ${vehicleInfoResponse.Type || 'N/A'}`,
          `Poids: ${vehicleInfoResponse.PoidsVide || 'N/A'} kg`,
          `Cylindres: ${vehicleInfoResponse.NbCylind || 'N/A'}`,
          `Cylindrée: ${vehicleInfoResponse.Cylindree || 'N/A'} CM3`,
          `Mode injection: ${vehicleInfoResponse.ModeInjection || 'N/A'}`,
          `Nombre de soupapes: ${vehicleInfoResponse.NbSoupapes || 'N/A'}`,
          `PTAC: ${vehicleInfoResponse.PTR || 'N/A'}`,
          `Code moteur: ${vehicleInfoResponse.CodeMoteur || 'N/A'}`,
          `N° Série: ${vehicleInfoResponse.NSerie || 'N/A'}`,
        ].join(', ');

        setValue('more_api_infos', moreApiInfos);

        setValue('vin', vehicleInfoResponse.VIN);
        setValue('model', vehicleInfoResponse.Modele);
        setValue('derivative', vehicleInfoResponse.Version);
        setValue('registration_plate', formatRegPlate(regPlateValue));
        setValue('seats', vehicleInfoResponse.NbPlacesAss);

        // Déterminez si le genre du véhicule correspond à une moto ou assimilé
        const isMoto = ['MTT1', 'MTT2', 'CYCL', 'MOT3', 'MOTO', 'MOT2', 'TQM'].includes(vehicleInfoResponse.Genre);

        // Si ce n'est pas une moto et que le nombre de portes est 0, utilisez le nombre de places assises
        if (!isMoto && vehicleInfoResponse.NbPortes === 0) {
          setValue('doors', vehicleInfoResponse.NbPlacesAss);
        } else {
          setValue('doors', vehicleInfoResponse.NbPortes);
        }

        const dateStr = vehicleInfoResponse.DateMec;
        const dateValue = dateStr ? dayjs(dateStr) : null;
        setValue('reg_date', dateValue);

        const genreValue = genreOptions.find((opt) => opt.label === vehicleInfoResponse.Genre);
        if (genreValue) {
          setValue('genre', { label: genreValue.label, value: genreValue.value });
        }

        const makeValue = options.find((opt) => opt.value === vehicleInfoResponse.Marque.toLowerCase());
        if (makeValue) {
          setValue('make', { label: makeValue.label, value: makeValue.value });
        }

        // Gestion de la couleur uniquement si elle est présente
        if (vehicleInfoResponse.Couleur) {
          const colourType = apiColourMapping[vehicleInfoResponse.Couleur] || vehicleInfoResponse.Couleur.toLowerCase();
          const colourValue = coloursOptions.find((opt) => opt.value === colourType);
          if (colourValue) {
            setValue('colour', { label: colourValue.label, value: colourValue.value });
          }
        }

        // Gestion du carburant
        const energyId = vehicleInfoResponse.energie;
        const energyText = vehicleInfoResponse.energieNGC || vehicleInfoResponse.Energie;

        // On essaie d'abord avec l'ID
        let mappedFuel = energyId ? apiEnergyMapping[energyId] : null;

        // Si pas de résultat avec l'ID, on essaie avec le texte
        if (!mappedFuel && energyText) {
          const normalizedEnergy = energyText.toUpperCase();
          mappedFuel = apiEnergyMapping[normalizedEnergy] || 'unknown';
        }

        if (mappedFuel) {
          const fuelValue = fuelOptions.find((opt) => opt.value === mappedFuel);
          if (fuelValue) {
            setValue('fuel', { label: fuelValue.label, value: fuelValue.value });
          }
        }

        // Gestion de la transmission
        if (vehicleInfoResponse.TPBoiteVit) {
          // Utiliser le mapping pour convertir la valeur de l'API
          const mappedTransmission = apiTransmissionMapping[vehicleInfoResponse.TPBoiteVit] || 'UNKNOWN';
          const transmissionValue = transmissionOptions.find((opt) => opt.value === mappedTransmission);
          if (transmissionValue) {
            setValue('transmission', { label: transmissionValue.label, value: transmissionValue.value });
          }
        }

        // Déclencher la validation pour tous les champs qui ont été mis à jour
        const fieldsToValidate = [
          'reg_plate',
          'vin',
          'model',
          'derivative',
          'seats',
          'doors',
          'reg_date',
          'genre',
          'make',
          'more_api_infos',
        ];

        // Ajouter les champs optionnels seulement s'ils ont été définis
        if (vehicleInfoResponse.Couleur) fieldsToValidate.push('colour');
        if (vehicleInfoResponse.energieNGC) fieldsToValidate.push('fuel');
        if (vehicleInfoResponse.TPBoiteVit) fieldsToValidate.push('transmission');

        await trigger(fieldsToValidate);

        return vehicleInfoResponse;
      }

      return null;
    };

    const handleGetRegPlateInformationsClick = async () => {
      setIsApiPlaqueLoading(true);
      try {
        const regPlateValue = getValues('reg_plate');
        const formattedRegPlate = formatRegPlate(regPlateValue);

        if (canBeFormatted(formattedRegPlate ?? '')) {
          setValue('reg_plate', formattedRegPlate);
        } else {
          setValue('reg_plate', regPlateValue);
        }

        await handleGetRegPlateInformations();
      } catch (error) {
        console.error('Error in handleGetRegPlateInformations:', error);
        setApiPlaqueError("Une erreur s'est produite lors de la récupération des informations.");
      } finally {
        setIsApiPlaqueLoading(false);
      }
    };

    useImperativeHandle(ref, () => ({
      handleGetRegPlateInformations,
    }));

    return (
      <Grid item container xs={12} sm={12}>
        <Grid item sm={12} md={6}>
          <Controller
            name="reg_plate"
            control={control}
            defaultValue=""
            render={({ field }) => (
              <TextField
                label="Immatriculation *"
                className="inputElement"
                error={!!errors.reg_plate}
                helperText={errors.reg_plate?.message}
                {...field}
                onChange={(e) => {
                  field.onChange(e);
                  if (e.target.value === '') {
                    setApiPlaqueError('');
                  }
                  // Réinitialiser les champs lorsque l'input change
                  resetApiFields();
                }}
              />
            )}
          />
        </Grid>
        <Grid item sm={12} md={6}>
          <Button
            variant="contained"
            color="primary"
            onClick={handleGetRegPlateInformationsClick}
            disabled={isApiPlaqueLoading}
            style={{ marginTop: '10px' }}
            className="dealActionButton"
          >
            {isApiPlaqueLoading ? 'Chargement...' : 'Récupérer les informations du véhicule'}
          </Button>
        </Grid>
      </Grid>
    );
  },
);

export default RegPlate;
